import * as React from "react";
import { withRouter, RouteComponentProps } from "react-router";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { selectSingleLineMode } from "../reducers/selectors";
import { translate } from "../../../IntlProviderWrapper";
import { SET_MAX_LINE_LABEL_LENGTH, GanttChartSource } from "../reducers/properties";
import { SET_RESCHEDULE_LINE_UUID, SET_LOCK_FILTER } from "../reducers/common";
import { RESET_FILTERS, SET_SELECTED_LINE_UUIDS } from "../reducers/reducersFilters/filters";
import { ReduxFilters } from "../reducers/BusinessLogic";
import { TypeFilterValues } from "../lineOrderList/filters/TypeFilterConsts";
import { ValidLinesOrdersHelper } from "../reducers/ValidLinesOrdersHelper";
import { publishSync, MSG_TYPES } from "../../../../lib/PubSub";

import type { SetRescheduleOrderAction } from "../reducers/common";
import type { PropertiesState, SetMaxLineLabelLengthAction } from "../reducers/properties";
import type { ResetFiltersAction, SetSelectedLineUuidsAction } from "../reducers/reducersFilters/filters";
import type { SetRescheduleLineAction, SetRescheduleOrderBundleAction, SetLockFilter } from "../reducers/common";
import type { SetLastClickedOrderUuid, SetHighlightedOrders } from "../reducers/planningTable";
import type { ReduxState } from "../reducers/reducers";

import img_eye_grey from "../../../../img/eye_grey.svg";
import img_eye_dark_grey from "../../../../img/eye_dark_grey.svg";
import LineStatus from "./LineStatus";

type ActionTypes =
    | SetMaxLineLabelLengthAction
    | SetRescheduleLineAction
    | SetRescheduleOrderAction
    | ResetFiltersAction
    | SetLastClickedOrderUuid
    | SetHighlightedOrders
    | SetRescheduleOrderBundleAction
    | SetLockFilter
    | SetSelectedLineUuidsAction;

type OwnProps = RouteComponentProps & {
    line_uuid: string;
    line_title: string;
    line_index: number;
    properties: PropertiesState;
    line_offset: number;
    is_unscheduled?: boolean;
    order_uuid?: string;
    is_workorder?: boolean;
    line_external_id?: string;
};

type Props = OwnProps & ReduxProps;

type State = {
    filter_line_icon_active: boolean;
};

class LineLabel extends React.Component<Props, State> {
    static LEFT_PADDING = 0;
    static FILTER_LINE_ICON_OFFSET = 22;
    static DOWNTIME_ICON_OFFSET = 17;
    static DOWNTIME_ICON_RADIUS = 5;

    static IMG_FILTER_LINE_ICON_DEFAULT = img_eye_grey;
    static IMG_FILTER_LINE_ICON_ACTIVE = img_eye_dark_grey;

    node = React.createRef<SVGTextElement>();

    state: State = {
        filter_line_icon_active: false,
    };

    componentDidMount() {
        if (this.node && this.node.current !== null) {
            this.props.reduxDispatch({
                type: SET_MAX_LINE_LABEL_LENGTH,
                data: this.node.current.getBBox().width + LineLabel.LEFT_PADDING,
            });
        }
    }

    isGantt(): boolean {
        return this.props.properties.source_type === GanttChartSource.gantt;
    }

    shouldShowLineStatus(): boolean {
        // Show only when we have a line name as well
        return !this.props.is_unscheduled && this.props.line_title.length > 0;
    }

    onClick = async () => {
        if (this.isGantt()) {
            this.props.history.push(`/manufacturing/lines/microplan/${this.props.line_uuid}`);
        } else {
            // clicking on label of unscheduled order is same as clicking on order
            const order_uuid = this.props.order_uuid;
            if (this.props.is_unscheduled && order_uuid) {
                ReduxFilters.setRescheduleOrderUuid(
                    this.props.reduxDispatch,
                    order_uuid,
                    "gantt",
                    this.props.is_filter_locked
                );
            } else if (!this.props.is_filter_locked) {
                // only works when filter is not locked
                if (this.props.is_reschedule_line) {
                    this.props.reduxDispatch({ type: RESET_FILTERS, data: undefined });
                } else {
                    this.props.reduxDispatch({ type: SET_RESCHEDULE_LINE_UUID, data: this.props.line_uuid });
                }
            }
        }
    };

    /**
     * Toggles a single line selection:
     * - If the line is already selected, it clears the selection and unlocks the filter;
     * - Otherwise, it selects the line and locks the filter.
     */
    handleFilterLine(line_uuid: string): void {
        const { reduxDispatch } = this.props;
        const single_line_mode = this.props.single_line_mode;

        reduxDispatch({ type: SET_SELECTED_LINE_UUIDS, data: single_line_mode ? [] : [line_uuid] });
        setTimeout(() => reduxDispatch({ type: SET_LOCK_FILTER, data: !single_line_mode }), 0);
        publishSync(MSG_TYPES.planning_table_align_scroll, { top: 0, left: 0, enforce: true });
        publishSync(MSG_TYPES.planning_table_align_scroll_end);
    }

    toggleActiveFilterLine(toggle: boolean): void {
        this.setState({ filter_line_icon_active: toggle });
    }

    render() {
        const line_uuid = this.props.line_uuid;
        let title = this.props.line_title;

        const properties: PropertiesState = this.props.properties;
        const source_type = properties.source_type;
        const title_len = title.length;
        const max_title_len = Math.floor(properties.space_for_label / 8.8); // 8.8 = 220/25
        let tooltip_title = title;

        if (this.props.line_external_id !== undefined && this.props.line_external_id !== "") {
            tooltip_title += " [" + this.props.line_external_id + "]";
        }

        if (title_len > max_title_len) {
            const last_word = title.substr(max_title_len - 6, title_len - 1);
            const last_chars = last_word.slice(-3);
            title = title.substr(0, max_title_len - 6) + "..." + last_chars;
        }

        const y0 = this.props.line_offset;

        const is_droppable = ReduxFilters.isDroppableLineUuid(
            this.props.line_uuid,
            this.props.last_clicked_order_uuid,
            this.props.is_unscheduled ?? false
        );

        let stroke: string | undefined;
        if ((this.props.is_reschedule_line && !this.props.is_unscheduled) || is_droppable) {
            stroke = "#616e7c";
        }

        let fill: string | undefined;
        const clicked_order_uuid = this.props.last_clicked_order_uuid || this.props.reschedule_order_uuid;
        if (clicked_order_uuid && !this.props.is_unscheduled) {
            const line_uuids = ValidLinesOrdersHelper.getPreferredLineUuids(clicked_order_uuid);
            if (line_uuids && line_uuids.includes(this.props.line_uuid)) {
                fill = "#FFD700";
            }
        }

        if (source_type === GanttChartSource.gantt) {
            tooltip_title =
                translate("common.tooltip_planningtable_line_gotomicroplan", "Go to Microplan for line") +
                ` ${tooltip_title}`;
        }

        let current_x_position = LineLabel.LEFT_PADDING;
        const getXPositionThenAdd = (offset: number): number => {
            const old_x_position = current_x_position;
            current_x_position += offset;
            return old_x_position;
        };

        const filter_line_icon =
            this.state.filter_line_icon_active || this.props.single_line_mode
                ? LineLabel.IMG_FILTER_LINE_ICON_ACTIVE
                : LineLabel.IMG_FILTER_LINE_ICON_DEFAULT;

        return (
            <g
                key={line_uuid}
                ref={this.node}
                name="line-label"
                line-index={this.props.line_index}
                line-uuid={line_uuid}
            >
                {!this.props.is_unscheduled && !this.isGantt() && title && (
                    <image
                        x={getXPositionThenAdd(LineLabel.FILTER_LINE_ICON_OFFSET)}
                        y={y0 + 7}
                        href={filter_line_icon}
                        height="16"
                        width="16"
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() => this.toggleActiveFilterLine(true)}
                        onMouseLeave={() => this.toggleActiveFilterLine(false)}
                        onClick={() => this.handleFilterLine(this.props.line_uuid)}
                    />
                )}
                {this.shouldShowLineStatus() && this.props.line_status && (
                    <LineStatus
                        standaloneSvg={false}
                        status={this.props.line_status}
                        cx={getXPositionThenAdd(LineLabel.DOWNTIME_ICON_OFFSET) + LineLabel.DOWNTIME_ICON_RADIUS}
                        cy={y0 + 15}
                        r={LineLabel.DOWNTIME_ICON_RADIUS}
                    />
                )}
                {fill && (
                    <rect
                        x={0}
                        y={y0}
                        width={properties.max_line_label_length ?? undefined}
                        height={properties.bar_height}
                        fill={fill}
                    ></rect>
                )}
                <text
                    key={line_uuid + "-text-" + (stroke || "")}
                    id={this.props.is_unscheduled ? "unscheduled-line-title" : "line-title"}
                    stroke={stroke}
                    textAnchor="start"
                    fontFamily="'Roboto', sans-serif"
                    style={{
                        fill: this.props.is_workorder ? "red" : "#616e7c",
                        cursor: "pointer",
                        fontSize: "14px",
                    }}
                    x={getXPositionThenAdd(0)}
                    y={y0 + 20}
                    onClick={this.onClick}
                    // @ts-ignore
                    line_uuid={line_uuid}
                    line_title={title}
                    line_index={this.props.line_index}
                    title={tooltip_title}
                >
                    {title}
                    <title>{tooltip_title}</title>
                </text>
            </g>
        );
    }
}

const mapState = (state: ReduxState, own_props: OwnProps) => {
    const planning_table = state.gantt_chart_planning_table;

    const filters = state.gantt_chart_filters;
    const is_reschedule_line =
        filters.selected_filter_type === TypeFilterValues.reschedule_line &&
        filters.reschedule_line_uuid === own_props.line_uuid;

    return {
        is_reschedule_line,
        last_clicked_order_uuid: planning_table.last_clicked_order_uuid,
        source_type: state.gantt_chart_properties.source_type,
        line_status: state.gantt_chart_insights.line_statuses.get(own_props.line_uuid),
        original_report_created_at: state.gantt_chart_report.original_report_created_at,
        is_filter_locked: filters.is_filter_locked,
        reschedule_order_uuid: filters.reschedule_order_uuid,
        selected_line_uuids: filters.selected_line_uuids,
        single_line_mode: selectSingleLineMode(state),
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default withRouter(
    connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
        mapState,
        mapDispatch
    )(LineLabel)
);
